import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaMobileAlt, FaPaintBrush, FaGlobe, FaBolt, FaChartBar, FaLock, FaSearch, FaHome, FaBox, FaCamera, FaClipboardList, FaUser } from 'react-icons/fa';
import SEO from '../../components/common/SEO';
import logoFooter from '../../assets/images/logo-footer.svg';

const benefitIcons = {
  1: <FaPaintBrush />,
  2: <FaMobileAlt />,
  3: <FaGlobe />,
  4: <FaBolt />,
  5: <FaChartBar />,
  6: <FaLock />
};

const MobileOrderPage = () => {
  const { t } = useTranslation();
  const { lang } = useParams();
  const [scanStep, setScanStep] = useState(1);
  const [buttonClicked, setButtonClicked] = useState(false);

  useEffect(() => {
    const timer1 = setTimeout(() => {
      setScanStep(2);
    }, 2500);
    
    const timer2 = setTimeout(() => {
      setButtonClicked(true);
      setTimeout(() => {
        setScanStep(3);
        setButtonClicked(false);
      }, 300);
    }, 5000);
    
    const timer3 = setTimeout(() => {
      setScanStep(1);
    }, 7500);
    
    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
    };
  }, [scanStep]);

  return (
    <>
      <SEO 
        titleKey="features.mobileOrder.seo.title"
        descriptionKey="features.mobileOrder.seo.description"
      />
      <PageContainer>
        <HeroSection>
          <HeroContent>
            <HeroTag>{t('features.mobileOrder.hero.tag')}</HeroTag>
            <HeroTitle>{t('features.mobileOrder.hero.title')}</HeroTitle>
            <HeroSubtitle>{t('features.mobileOrder.hero.subtitle')}</HeroSubtitle>
            <CTAButtons>
              <PrimaryButton to={`/${lang}/demo`}>
                {t('features.mobileOrder.hero.ctaPrimary')}
              </PrimaryButton>
              <SecondaryButton to={`/${lang}/contact`}>
                {t('features.mobileOrder.hero.ctaSecondary')}
              </SecondaryButton>
            </CTAButtons>
          </HeroContent>
          <HeroImageContainer>
            <HeroIcon><FaMobileAlt /></HeroIcon>
          </HeroImageContainer>
        </HeroSection>

        <FeaturesSection>
          <SectionTitle>{t('features.mobileOrder.benefits.title')}</SectionTitle>
          <FeaturesGrid>
            {[1, 2, 3, 4, 5, 6].map((num) => (
              <FeatureCard key={num}>
                <FeatureIcon>{benefitIcons[num]}</FeatureIcon>
                <FeatureTitle>{t(`features.mobileOrder.benefits.items.benefit${num}.title`)}</FeatureTitle>
                <FeatureDescription>
                  {t(`features.mobileOrder.benefits.items.benefit${num}.description`)}
                </FeatureDescription>
              </FeatureCard>
            ))}
          </FeaturesGrid>
        </FeaturesSection>

        <BarcodeScanningSection>
          <BarcodeContent>
            <BarcodeTextContent>
              <BarcodeTag>{t('features.mobileOrder.barcodeScanning.tag')}</BarcodeTag>
              <BarcodeTitle>{t('features.mobileOrder.barcodeScanning.title')}</BarcodeTitle>
              <BarcodeDescription>{t('features.mobileOrder.barcodeScanning.description')}</BarcodeDescription>
              <BarcodeFeaturesList>
                {[1, 2, 3, 4].map((num) => (
                  <BarcodeFeatureItem key={num}>
                    <BarcodeCheckIcon>✓</BarcodeCheckIcon>
                    <span>{t(`features.mobileOrder.barcodeScanning.features.feature${num}`)}</span>
                  </BarcodeFeatureItem>
                ))}
              </BarcodeFeaturesList>
              <BarcodeHighlight>{t('features.mobileOrder.barcodeScanning.highlight')}</BarcodeHighlight>
            </BarcodeTextContent>
            <BarcodeVisualContent>
              <PhoneMockup>
                <PhoneScreen>
                  <AppHeader>
                    <AppLogo>
                      <img src={logoFooter} alt="PetekB2B" loading="lazy" />
                    </AppLogo>
                    <AppHeaderIcons>
                      <FaSearch />
                    </AppHeaderIcons>
                  </AppHeader>
                  
                  <AppContent>
                    {scanStep === 1 && (
                      <>
                        <ScannerArea>
                          <ScannerFrame>
                            <ScannerCorner position="top-left" />
                            <ScannerCorner position="top-right" />
                            <ScannerCorner position="bottom-left" />
                            <ScannerCorner position="bottom-right" />
                            <ScanLine />
                          </ScannerFrame>
                          <ScannerText>{t('features.mobileOrder.barcodeScanning.scanPrompt')}</ScannerText>
                        </ScannerArea>
                        
                        <BarcodeExample show={true}>
                          <BarcodeStripes>
                            {[...Array(20)].map((_, i) => (
                              <BarcodeStripe key={i} width={Math.random() > 0.5 ? '3px' : '6px'} />
                            ))}
                          </BarcodeStripes>
                          <BarcodeNumber>8 690504 123456</BarcodeNumber>
                        </BarcodeExample>
                      </>
                    )}
                    
                    {scanStep === 2 && (
                      <ProductCard show={true}>
                        <ProductImage>
                          <FaBox />
                        </ProductImage>
                        <ProductName>Kaşar Peyniri Premium</ProductName>
                        <ProductCode>Ürün Kodu: 8690504123456</ProductCode>
                        <StockStatus>✓ Stokta Var (250 adet)</StockStatus>
                        <ProductPrice>₺485,00 / kg</ProductPrice>
                        <AddToCartButton clicked={buttonClicked}>
                          Sepete Ekle
                        </AddToCartButton>
                      </ProductCard>
                    )}
                    
                    <SuccessMessage show={scanStep === 3}>
                      {t('ecommerce.barcodeScanner.success') !== 'ecommerce.barcodeScanner.success' 
                        ? t('ecommerce.barcodeScanner.success') 
                        : 'Ürün sepete eklendi!'}
                    </SuccessMessage>
                  </AppContent>
                  
                  <BottomNav>
                    <NavItem>
                      <FaHome />
                      <span>Ana Sayfa</span>
                    </NavItem>
                    <NavItem>
                      <FaBox />
                      <span>Ürünler</span>
                    </NavItem>
                    <NavItem active>
                      <FaCamera />
                      <span>Tara</span>
                    </NavItem>
                    <NavItem>
                      <FaClipboardList />
                      <span>Siparişler</span>
                    </NavItem>
                    <NavItem>
                      <FaUser />
                      <span>Hesabım</span>
                    </NavItem>
                  </BottomNav>
                </PhoneScreen>
              </PhoneMockup>
            </BarcodeVisualContent>
          </BarcodeContent>
        </BarcodeScanningSection>

        <HowItWorksSection>
          <SectionTitle>{t('features.mobileOrder.howItWorks.title')}</SectionTitle>
          <StepsContainer>
            {[1, 2, 3, 4].map((num) => (
              <StepCard key={num}>
                <StepNumber>{num}</StepNumber>
                <StepContent>
                  <StepTitle>{t(`features.mobileOrder.howItWorks.steps.step${num}.title`)}</StepTitle>
                  <StepDescription>
                    {t(`features.mobileOrder.howItWorks.steps.step${num}.description`)}
                  </StepDescription>
                </StepContent>
              </StepCard>
            ))}
          </StepsContainer>
        </HowItWorksSection>

        <CTASection>
          <CTAContent>
            <CTATitle>{t('features.mobileOrder.cta.title')}</CTATitle>
            <CTADescription>{t('features.mobileOrder.cta.description')}</CTADescription>
            <CTAButton to={`/${lang}/demo`}>
              {t('features.mobileOrder.cta.button')}
            </CTAButton>
          </CTAContent>
        </CTASection>
      </PageContainer>
    </>
  );
};

const PageContainer = styled.div`
  min-height: 100vh;
`;

const HeroSection = styled.section`
  background: linear-gradient(135deg, #30cfd0 0%, #330867 100%);
  padding: 120px 5% 80px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: center;

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    padding: 100px 5% 60px;
    text-align: center;
  }
`;

const HeroContent = styled.div`
  color: white;
`;

const HeroTag = styled.span`
  display: inline-block;
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  padding: 0.5rem 1rem;
  border-radius: 50px;
  font-size: 0.875rem;
  font-weight: 600;
  margin-bottom: 1.5rem;
`;

const HeroTitle = styled.h1`
  font-size: 3rem;
  font-weight: 800;
  margin-bottom: 1.5rem;
  line-height: 1.2;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.25rem;
  opacity: 0.95;
  margin-bottom: 2rem;
  line-height: 1.6;

  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const CTAButtons = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;

  @media (max-width: 968px) {
    justify-content: center;
  }
`;

const PrimaryButton = styled(Link)`
  background: white;
  color: #330867;
  padding: 1rem 2rem;
  border-radius: 50px;
  font-weight: 700;
  text-decoration: none;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.3);
  }
`;

const SecondaryButton = styled(Link)`
  background: transparent;
  color: white;
  padding: 1rem 2rem;
  border-radius: 50px;
  font-weight: 700;
  text-decoration: none;
  border: 2px solid white;
  transition: all 0.3s ease;

  &:hover {
    background: white;
    color: #330867;
  }
`;

const HeroImageContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;

  @media (max-width: 968px) {
    margin-top: 2rem;
  }
`;

const HeroIcon = styled.div`
  font-size: 15rem;
  filter: drop-shadow(0 10px 30px rgba(0, 0, 0, 0.3));
  animation: pulse 2s ease-in-out infinite;

  @keyframes pulse {
    0%, 100% {
      transform: scale(1);
    }
    50% {
      transform: scale(1.05);
    }
  }

  @media (max-width: 768px) {
    font-size: 8rem;
  }
`;

const FeaturesSection = styled.section`
  padding: 80px 5%;
  background: #f8f9fa;
`;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 800;
  text-align: center;
  margin-bottom: 4rem;
  color: #1a202c;

  @media (max-width: 768px) {
    font-size: 1.75rem;
    margin-bottom: 2rem;
  }
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  max-width: 1200px;
  margin: 0 auto;
`;

const FeatureCard = styled.div`
  background: white;
  padding: 2rem;
  border-radius: 20px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
  }
`;

const FeatureIcon = styled.div`
  font-size: 3rem;
  margin-bottom: 1rem;
  text-align: center;
  color: ${props => props.theme?.colors?.primary || '#dc2626'};

  svg {
    width: 3rem;
    height: 3rem;
  }
`;

const FeatureTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 1rem;
  color: #1a202c;
`;

const FeatureDescription = styled.p`
  color: #4a5568;
  line-height: 1.6;
`;

const HowItWorksSection = styled.section`
  padding: 80px 5%;
  background: white;
`;

const StepsContainer = styled.div`
  max-width: 900px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const StepCard = styled.div`
  display: flex;
  gap: 2rem;
  align-items: flex-start;
  padding: 2rem;
  background: #f8f9fa;
  border-radius: 20px;
  transition: all 0.3s ease;

  &:hover {
    background: linear-gradient(135deg, #30cfd0 0%, #330867 100%);
    color: white;

    h3, p {
      color: white;
    }
  }

  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
    align-items: center;
  }
`;

const StepNumber = styled.div`
  font-size: 3rem;
  font-weight: 800;
  color: #30cfd0;
  min-width: 80px;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: white;
  border-radius: 20px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);

  ${StepCard}:hover & {
    color: white;
    background: rgba(255, 255, 255, 0.2);
  }
`;

const StepContent = styled.div`
  flex: 1;
`;

const StepTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
  color: #1a202c;
`;

const StepDescription = styled.p`
  color: #4a5568;
  line-height: 1.6;
`;

const CTASection = styled.section`
  background: linear-gradient(135deg, #30cfd0 0%, #330867 100%);
  padding: 80px 5%;
`;

const CTAContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
  text-align: center;
  color: white;
`;

const CTATitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 800;
  margin-bottom: 1.5rem;

  @media (max-width: 768px) {
    font-size: 1.75rem;
  }
`;

const CTADescription = styled.p`
  font-size: 1.25rem;
  margin-bottom: 2rem;
  opacity: 0.95;

  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const CTAButton = styled(Link)`
  display: inline-block;
  background: white;
  color: #330867;
  padding: 1rem 3rem;
  border-radius: 50px;
  font-weight: 700;
  font-size: 1.125rem;
  text-decoration: none;
  transition: all 0.3s ease;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 25px rgba(0, 0, 0, 0.3);
  }
`;

const BarcodeScanningSection = styled.section`
  padding: 100px 5%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: -50%;
    right: -10%;
    width: 500px;
    height: 500px;
    background: rgba(255, 255, 255, 0.1);
    border-radius: 50%;
    filter: blur(60px);
  }
`;

const BarcodeContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: center;
  position: relative;
  z-index: 1;

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    gap: 3rem;
  }
`;

const BarcodeTextContent = styled.div`
  color: white;
`;

const BarcodeTag = styled.span`
  display: inline-block;
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  padding: 0.5rem 1.5rem;
  border-radius: 50px;
  font-size: 0.875rem;
  font-weight: 600;
  margin-bottom: 1.5rem;
`;

const BarcodeTitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 800;
  margin-bottom: 1.5rem;
  line-height: 1.2;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const BarcodeDescription = styled.p`
  font-size: 1.125rem;
  line-height: 1.8;
  margin-bottom: 2rem;
  opacity: 0.95;
`;

const BarcodeFeaturesList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 2rem 0;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const BarcodeFeatureItem = styled.li`
  display: flex;
  align-items: center;
  gap: 1rem;
  font-size: 1.125rem;
  font-weight: 500;
`;

const BarcodeCheckIcon = styled.span`
  width: 30px;
  height: 30px;
  background: rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  flex-shrink: 0;
`;

const BarcodeHighlight = styled.div`
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(10px);
  padding: 1.5rem;
  border-radius: 15px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  font-size: 1.125rem;
  font-weight: 600;
  text-align: center;
`;

const BarcodeVisualContent = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  
  @media (max-width: 968px) {
    order: -1;
  }
`;

const scanAnimation = keyframes`
  0% { top: 10%; opacity: 0; }
  50% { opacity: 1; }
  100% { top: 90%; opacity: 0; }
`;

const successPulse = keyframes`
  0%, 100% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.1); opacity: 0.8; }
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const buttonClickAnim = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(0.95); }
  100% { transform: scale(1); }
`;

const PhoneMockup = styled.div`
  width: 320px;
  height: 640px;
  background: #1a1a1a;
  border-radius: 40px;
  padding: 12px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.4);
  position: relative;

  &:before {
    content: '';
    position: absolute;
    top: 20px;
    left: 50%;
    transform: translateX(-50%);
    width: 60px;
    height: 6px;
    background: #333;
    border-radius: 3px;
  }

  @media (max-width: 768px) {
    width: 280px;
    height: 560px;
  }
`;

const PhoneScreen = styled.div`
  width: 100%;
  height: 100%;
  background: #f5f5f5;
  border-radius: 32px;
  overflow: hidden;
  position: relative;
  display: flex;
  flex-direction: column;
`;

const AppHeader = styled.div`
  background: #dc2626;
  padding: 1rem;
  padding-top: 2.5rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  position: relative;
  z-index: 10;
`;

const AppLogo = styled.div`
  display: flex;
  align-items: center;
  height: 30px;
  
  img {
    height: 100%;
    width: auto;
    object-fit: contain;
  }
`;

const AppHeaderIcons = styled.div`
  display: flex;
  gap: 0.8rem;
  color: white;
  font-size: 1.2rem;
  
  svg {
    cursor: pointer;
    transition: transform 0.2s ease;
    &:hover { transform: scale(1.1); }
  }
`;

const AppContent = styled.div`
  flex: 1;
  overflow: hidden;
  position: relative;
  background: #f5f5f5;
`;

const BottomNav = styled.div`
  background: white;
  display: flex;
  justify-content: space-around;
  padding: 0.8rem 0;
  border-top: 1px solid #e0e0e0;
  position: relative;
  z-index: 10;
`;

const NavItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  font-size: 0.7rem;
  color: ${props => props.active ? '#dc2626' : '#999'};
  gap: 0.2rem;
  
  svg { font-size: 1.3rem; }
`;

const ScannerArea = styled.div`
  position: absolute;
  top: 30%;
  left: 50%;
  transform: translateX(-50%);
  width: 80%;
  height: 200px;
  background: rgba(102, 126, 234, 0.1);
  border-radius: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border: 3px dashed #dc2626;
`;

const ScannerFrame = styled.div`
  width: 200px;
  height: 150px;
  position: relative;
  margin-bottom: 1rem;
`;

const ScannerCorner = styled.div`
  position: absolute;
  width: 30px;
  height: 30px;
  border: 3px solid #dc2626;
  
  ${props => props.position === 'top-left' && `top: 0; left: 0; border-right: none; border-bottom: none;`}
  ${props => props.position === 'top-right' && `top: 0; right: 0; border-left: none; border-bottom: none;`}
  ${props => props.position === 'bottom-left' && `bottom: 0; left: 0; border-right: none; border-top: none;`}
  ${props => props.position === 'bottom-right' && `bottom: 0; right: 0; border-left: none; border-top: none;`}
`;

const ScanLine = styled.div`
  position: absolute;
  width: 100%;
  height: 3px;
  background: linear-gradient(90deg, transparent, #dc2626, transparent);
  animation: ${scanAnimation} 2s ease-in-out infinite;
  box-shadow: 0 0 10px #dc2626;
`;

const ScannerText = styled.div`
  color: #dc2626;
  font-weight: 600;
  font-size: 0.9rem;
`;

const BarcodeExample = styled.div`
  position: absolute;
  bottom: 20%;
  left: 50%;
  transform: translateX(-50%);
  background: white;
  padding: 1rem;
  border-radius: 10px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  opacity: ${props => props.show ? 1 : 0};
  transition: opacity 0.3s ease;
`;

const BarcodeStripes = styled.div`
  display: flex;
  gap: 2px;
  padding: 0.5rem;
  background: white;
  border-radius: 5px;
`;

const BarcodeStripe = styled.div`
  width: ${props => props.width || '3px'};
  height: 60px;
  background: #000;
`;

const BarcodeNumber = styled.div`
  text-align: center;
  font-size: 0.85rem;
  font-weight: 600;
  margin-top: 0.5rem;
  letter-spacing: 2px;
`;

const ProductCard = styled.div`
  position: absolute;
  top: 10%;
  left: 50%;
  transform: translateX(-50%);
  width: 85%;
  background: white;
  border-radius: 15px;
  padding: 1.5rem;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
  opacity: ${props => props.show ? 1 : 0};
  animation: ${props => props.show ? fadeIn : 'none'} 0.5s ease;
  transition: opacity 0.3s ease;
`;

const ProductImage = styled.div`
  width: 100%;
  height: 120px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 3rem;
  color: white;
  margin-bottom: 1rem;
`;

const ProductName = styled.h4`
  font-size: 1.1rem;
  font-weight: 700;
  color: #1a1a1a;
  margin-bottom: 0.5rem;
`;

const ProductCode = styled.p`
  font-size: 0.85rem;
  color: #666;
  margin-bottom: 0.5rem;
`;

const ProductPrice = styled.div`
  font-size: 1.3rem;
  font-weight: 800;
  color: #dc2626;
  margin-bottom: 1rem;
`;

const StockStatus = styled.div`
  display: inline-block;
  background: #10b981;
  color: white;
  padding: 0.3rem 0.8rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  margin-bottom: 1rem;
`;

const AddToCartButton = styled.button`
  width: 100%;
  background: #dc2626;
  color: white;
  border: none;
  padding: 1rem;
  border-radius: 10px;
  font-size: 1rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s ease;
  animation: ${props => props.clicked ? buttonClickAnim : 'none'} 0.3s ease;
`;

const SuccessMessage = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: #10b981;
  color: white;
  padding: 1.5rem 2rem;
  border-radius: 15px;
  font-weight: 700;
  font-size: 1.1rem;
  box-shadow: 0 10px 30px rgba(16, 185, 129, 0.3);
  animation: ${successPulse} 1s ease-in-out;
  display: ${props => props.show ? 'flex' : 'none'};
  align-items: center;
  gap: 0.5rem;
  
  &:before {
    content: '✓';
    font-size: 1.5rem;
  }
`;

export default MobileOrderPage;

